import { useState } from 'react';
import { Link } from 'react-router-dom';

const Portfolio = () => {
  const [filter, setFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'strategy', label: 'Strategy' },
    { id: 'digital', label: 'Digital' },
    { id: 'operations', label: 'Operations' },
    { id: 'manda', label: 'M&A' },
  ];

  const projects = [
    {
      id: 1,
      category: 'strategy',
      title: 'Global Market Expansion',
      client: 'Fortune 100 Technology Company',
      description: 'Developed comprehensive market entry strategy for Asia-Pacific expansion.',
      results: ['40% Revenue Growth', '12 New Markets', '$2B Pipeline'],
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
    },
    {
      id: 2,
      category: 'digital',
      title: 'Digital Transformation',
      client: 'Leading Financial Services Firm',
      description: 'Led end-to-end digital transformation enabling seamless omnichannel experience.',
      results: ['60% Cost Reduction', '3x Customer Satisfaction', '18-month ROI'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop',
    },
    {
      id: 3,
      category: 'operations',
      title: 'Supply Chain Optimization',
      client: 'Global Manufacturing Leader',
      description: 'Redesigned supply chain operations reducing costs and improving delivery.',
      results: ['25% Cost Savings', '35% Faster Delivery', '99.2% Accuracy'],
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=600&fit=crop',
    },
    {
      id: 4,
      category: 'manda',
      title: 'Strategic Acquisition',
      client: 'Healthcare Technology Company',
      description: 'Managed $500M acquisition and post-merger integration.',
      results: ['$500M Deal Value', '120% Synergy Capture', '6-month Integration'],
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop',
    },
    {
      id: 5,
      category: 'strategy',
      title: 'Competitive Repositioning',
      client: 'Consumer Goods Corporation',
      description: 'Developed new brand strategy and market positioning for emerging segments.',
      results: ['15% Market Share', '2x Brand Awareness', '$800M Revenue'],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
    },
    {
      id: 6,
      category: 'digital',
      title: 'AI Analytics Platform',
      client: 'E-commerce Giant',
      description: 'Built predictive analytics platform leveraging AI for inventory optimization.',
      results: ['45% Better Predictions', '30% Less Waste', '25% Higher Conversion'],
      image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&h=600&fit=crop',
    },
  ];

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(p => p.category === filter);

  const testimonials = [
    {
      quote: "Nexus transformed our entire approach to digital strategy. Their expertise resulted in a 300% improvement in our conversion rates.",
      author: 'Jennifer Martinez',
      role: 'Chief Digital Officer',
      company: 'TechCorp International',
    },
    {
      quote: "The strategic insights provided by Nexus were instrumental in our successful expansion into Asian markets.",
      author: 'Robert Chang',
      role: 'CEO',
      company: 'GlobalTrade Solutions',
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Case Studies</p>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Proven <span className="text-primary">Results</span>
            </h1>
            <p className="text-xl text-slate-400">
              Real-world success stories showcasing our impact across industries.
              See how we've helped organizations achieve transformational outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8">
        <div className="section-container">
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === f.id
                    ? 'bg-primary text-white'
                    : 'bg-dark-800 text-slate-400 hover:text-white border border-white/5'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-24">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-6">
            {filteredProjects.map((project) => (
              <div key={project.id} className="card overflow-hidden group">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 lg:p-8">
                  <span className="text-xs text-primary font-medium uppercase tracking-wider">
                    {project.category}
                  </span>
                  <h3 className="text-xl font-semibold mt-2 mb-1 font-sans">{project.title}</h3>
                  <p className="text-slate-500 text-sm mb-3">{project.client}</p>
                  <p className="text-slate-400 mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.results.map((result, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                      >
                        {result}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-dark-800">
        <div className="section-container">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Testimonials</p>
            <h2 className="text-3xl lg:text-4xl font-bold">What Clients Say</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-dark-900 rounded-2xl p-8 border border-white/5">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-lg text-slate-300 mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold font-sans">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="section-container">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Ready to Be Our Next Success Story?</h2>
            <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help your organization achieve similar results.
            </p>
            <Link to="/contact" className="btn-primary">
              Start Your Project
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
